import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip, priorityChip, statusChip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, MessageSquare, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/exports";

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [comment, setComment] = useState("");
  const [form, setForm] = useState({ subject: "", description: "", contact_id: "", priority: "medium", status: "open", business_unit: "FigureWeb", assignee: "" });

  const load = () => api.get("/tickets").then(r => setTickets(r.data));
  useEffect(() => {
    load();
    api.get("/contacts").then(r => setContacts(r.data));
    api.get("/users").then(r => setUsers(r.data));
  }, []);

  const create = async () => {
    if (!form.subject) return toast.error("Subject required");
    const c = contacts.find(x => x.id === form.contact_id);
    await api.post("/tickets", { ...form, contact_name: c?.name || "", business_unit: c?.business_unit || form.business_unit });
    toast.success("Ticket created");
    setOpen(false);
    setForm({ subject: "", description: "", contact_id: "", priority: "medium", status: "open", business_unit: "FigureWeb", assignee: "" });
    load();
  };

  const openDetail = async (t) => {
    setActive(t);
    setDetailOpen(true);
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/tickets/${id}`, { status });
    toast.success("Updated");
    load();
    if (active?.id === id) setActive({ ...active, status });
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    const r = await api.post(`/tickets/${active.id}/comments`, { text: comment, internal: true });
    setActive({ ...active, comments: [...(active.comments || []), r.data] });
    setComment("");
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Support Tickets"
        subtitle={`${tickets.filter(t=>t.status!=="closed").length} active · ${tickets.filter(t=>t.priority==="high").length} high priority`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => downloadCsv("tickets")} data-testid="export-tickets-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-ticket-btn" className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">
                <Plus className="w-4 h-4 mr-1" /> New Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle>New Ticket</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Subject</Label>
                  <Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} data-testid="ticket-subject-input" /></div>
                <div><Label>Description</Label>
                  <Textarea rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
                <div><Label>Contact</Label>
                  <Select value={form.contact_id} onValueChange={v => setForm({...form, contact_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select contact" /></SelectTrigger>
                    <SelectContent>
                      {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name} — {c.company}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div><Label>Assign to</Label>
                    <Select value={form.assignee} onValueChange={v => setForm({...form, assignee: v})}>
                      <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={create} data-testid="save-ticket-btn" className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="table-head">
            <tr>
              <th className="text-left px-4 py-3">Subject</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Unit</th>
              <th className="text-left px-4 py-3">Priority</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tickets.map(t => (
              <tr key={t.id} className="row cursor-pointer" onClick={() => openDetail(t)} data-testid={`ticket-row-${t.id}`}>
                <td className="px-4 py-3 font-medium text-slate-800">{t.subject}</td>
                <td className="px-4 py-3 text-slate-600">{t.contact_name || "—"}</td>
                <td className="px-4 py-3"><Chip>{t.business_unit}</Chip></td>
                <td className="px-4 py-3"><Chip variant={priorityChip(t.priority)}>{t.priority}</Chip></td>
                <td className="px-4 py-3"><Chip variant={statusChip(t.status)}>{t.status.replace("_"," ")}</Chip></td>
                <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(t.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <MessageSquare className="w-4 h-4 text-slate-400 inline" />
                  <span className="text-xs text-slate-400 ml-1 num">{t.comments?.length || 0}</span>
                </td>
              </tr>
            ))}
            {tickets.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-500 text-sm">No tickets yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{active?.subject}</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Chip variant={priorityChip(active.priority)}>{active.priority}</Chip>
                <Chip variant={statusChip(active.status)}>{active.status.replace("_"," ")}</Chip>
                <Chip>{active.business_unit}</Chip>
                <Chip variant="info">{active.contact_name}</Chip>
              </div>
              <div className="text-sm text-slate-700 bg-slate-50 rounded-md p-3">{active.description}</div>

              <div className="flex items-center gap-2">
                <Label className="text-xs">Update status</Label>
                <Select value={active.status} onValueChange={(v) => updateStatus(active.id, v)}>
                  <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Internal comments</div>
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {(active.comments || []).length === 0 && <div className="text-xs text-slate-400">No comments yet.</div>}
                  {(active.comments || []).map(c => (
                    <div key={c.id} className="bg-brand-aqua-light border border-brand-aqua/20 rounded-md px-3 py-2">
                      <div className="text-xs text-slate-500 mb-0.5"><strong className="text-slate-700">{c.by}</strong> · {formatDate(c.at)}</div>
                      <div className="text-sm text-slate-800">{c.text}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <Input placeholder="Add internal comment..." value={comment} onChange={e => setComment(e.target.value)}
                         onKeyDown={e => e.key === "Enter" && addComment()} data-testid="ticket-comment-input" />
                  <Button onClick={addComment} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-ticket-comment-btn">Post</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
